/* News_UI - Diseño mejorado profesional y moderno */
import React from 'react';
import { Button, Card, CardBody, CardFooter, CardHeader } from "@material-tailwind/react";
import { Calendar, ArrowRight, ChevronLeft, ChevronRight, Clock } from "lucide-react";

const NewsUI = ({
  language,
  noticias,
  paginaActual,
  totalPaginas,
  onPaginaAnterior,
  onPaginaSiguiente,
  onLeerMas
}) => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Header */}
      <div className="bg-gradient-to-br from-green-600 via-green-700 to-teal-700 text-white py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="inline-block bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-4">
            <span className="text-sm font-semibold uppercase tracking-wider">
              {language === 'es' ? 'Últimas Actualizaciones' : 'Latest Updates'}
            </span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-4">
            {language === 'es' ? 'Ultimas Noticias ' : 'Last News'}
          </h1>
          <p className="text-lg md:text-xl text-green-50 max-w-3xl">
            {language === 'es' 
              ? 'Mantente al día con los últimos avances y desarrollos de nuestro proyecto VLIR-UOS'
              : 'Stay up to date with the latest advances and developments of our VLIR-UOS project'
            }
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Featured Article (Primera noticia) */}
        {noticias.length > 0 && (
          <Card className="mb-12 overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 border-0">
            <div className="md:flex">
              <CardHeader 
                floated={false} 
                className="md:w-1/2 h-96 m-0 rounded-none relative group overflow-hidden"
              >
                <img 
                  src={noticias[0][1].image} 
                  alt={noticias[0][1].title[language]} 
                  className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700" 
                />
                <div className="absolute top-4 left-4">
                  <span className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-bold shadow-lg">
                    {language === 'es' ? 'DESTACADO' : 'FEATURED'}
                  </span>
                </div>
              </CardHeader>
              
              <div className="md:w-1/2 flex flex-col justify-center">
                <CardBody className="p-8 lg:p-12">
                  <div className="flex items-center gap-3 text-gray-600 mb-4">
                    <Calendar size={18} className="text-green-600" />
                    <span className="text-sm font-medium">{noticias[0][1].date}</span>
                    <span className="text-gray-300">•</span>
                    <Clock size={18} className="text-green-600" />
                    <span className="text-sm font-medium">
                      {language === 'es' ? '5 min lectura' : '5 min read'}
                    </span>
                  </div>
                  
                  <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4 leading-tight">
                    {noticias[0][1].title[language]}
                  </h2>
                  
                  <p className="text-gray-600 text-lg leading-relaxed mb-6">
                    {noticias[0][1].content[language].slice(0, 250)}...
                  </p>
                  
                  <Button 
                    className="bg-green-600 hover:bg-green-700 shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2 normal-case text-base px-6 py-3" 
                    onClick={() => onLeerMas(noticias[0][0], noticias[0][1])}
                  >
                    {language === 'es' ? 'Leer artículo completo' : 'Read full article'}
                    <ArrowRight size={20} />
                  </Button>
                </CardBody>
              </div>
            </div>
          </Card>
        )}
        
        {/* Grid de noticias restantes */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {noticias.slice(1).map(([id, noticia]) => {
            const title = noticia.title[language];
            const content = noticia.content[language];

            return (
              <Card 
                key={id} 
                className="overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-0 bg-white"
              >
                <CardHeader 
                  floated={false} 
                  className="h-56 m-0 rounded-none relative group overflow-hidden"
                >
                  <img 
                    src={noticia.image} 
                    alt={title} 
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500" 
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </CardHeader>
                
                <CardBody className="p-6">
                  <div className="flex items-center gap-2 text-green-600 mb-3">
                    <Calendar size={16} />
                    <span className="text-sm font-medium">{noticia.date}</span>
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 leading-tight hover:text-green-600 transition-colors">
                    {title}
                  </h3>
                  
                  <p className="text-gray-600 text-sm leading-relaxed line-clamp-3">
                    {content.slice(0, 150)}...
                  </p>
                </CardBody>
                
                <CardFooter className="p-6 pt-0">
                  <Button 
                    variant="text"
                    className="text-green-600 hover:bg-green-50 p-0 flex items-center gap-2 normal-case font-semibold group" 
                    onClick={() => onLeerMas(id, noticia)}
                  >
                    {language === 'es' ? 'Leer más' : 'Read more'}
                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>
        
        {/* Paginación mejorada */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-6 bg-white p-6 rounded-xl shadow-md">
          <Button
            variant="outlined"
            className="border-2 border-green-600 text-green-600 hover:bg-green-50 transition-all duration-300 flex items-center gap-2 font-semibold normal-case px-6 py-3"
            onClick={onPaginaAnterior}
            disabled={paginaActual === 1}
          >
            <ChevronLeft size={20} />
            {language === 'es' ? 'Anterior' : 'Previous'}
          </Button>
          
          <div className="flex flex-col items-center gap-3">
            <div className="flex items-center gap-2">
              {[...Array(totalPaginas)].map((_, i) => (
                <button
                  key={i}
                  onClick={() => {}}
                  className={`transition-all duration-300 rounded-full ${
                    i + 1 === paginaActual 
                      ? 'bg-green-600 w-10 h-3' 
                      : 'bg-gray-300 w-3 h-3 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>
            <span className="text-gray-700 font-medium text-sm">
              {language === 'es' 
                ? `Página ${paginaActual} de ${totalPaginas}` 
                : `Page ${paginaActual} of ${totalPaginas}`
              }
            </span>
          </div>
          
          <Button
            className="bg-green-600 hover:bg-green-700 shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2 font-semibold normal-case px-6 py-3"
            onClick={onPaginaSiguiente}
            disabled={paginaActual === totalPaginas}
          >
            {language === 'es' ? 'Siguiente' : 'Next'}
            <ChevronRight size={20} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NewsUI;